import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Paperclip, SendHorizontal, Zap } from "lucide-react";

export const ChatComposer = ({ session, onSent }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [replies, setReplies] = useState([]);
  const [open, setOpen] = useState(false);
  const file = useRef(null);
  const ping = useRef(0);
  useEffect(() => { api.get("/chat/replies").then((r) => setReplies(r.data)).catch(() => {}); }, []);
  useEffect(() => { setText(""); }, [session.id]);

  const first = (session.name || "").split(" ")[0];
  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try { await api.post(`/chat/sessions/${session.id}/messages`, { text: t }); setText(""); onSent(); }
    catch (e) { toast.error(apiError(e, "Não foi possível enviar")); }
    setBusy(false);
  };
  const type = (v) => {
    setText(v);
    if (Date.now() - ping.current > 3000) { ping.current = Date.now(); api.post(`/chat/sessions/${session.id}/typing`).catch(() => {}); }
  };
  const upload = async (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("Arquivo maior que 10 MB");
    const fd = new FormData(); fd.append("file", f);
    const id = toast.loading("Enviando arquivo...");
    try { await api.post(`/chat/sessions/${session.id}/upload`, fd); toast.success("Arquivo enviado", { id }); onSent(); }
    catch (e) { toast.error(apiError(e, "Falha no envio do arquivo"), { id }); }
  };

  return (
    <div className="flex items-end gap-2 border-t border-[#1F293D] bg-[#0F141F] p-3">
      <input ref={file} type="file" hidden data-testid="chat-file-input" accept="image/*,.pdf,.doc,.docx,.txt" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
      <button data-testid="chat-attach-btn" onClick={() => file.current.click()} title="Enviar arquivo" className="rounded-lg border border-[#1F293D] p-2.5 text-slate-400 transition-colors hover:text-white"><Paperclip className="h-4 w-4" /></button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button data-testid="chat-quick-replies-btn" title="Respostas prontas" className="rounded-lg border border-[#1F293D] p-2.5 text-amber-300 transition-colors hover:bg-amber-500/10"><Zap className="h-4 w-4" /></button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 border-[#1F293D] bg-[#111827] p-2">
          <div className="max-h-72 space-y-1 overflow-y-auto">
            {replies.map((r) => (
              <button key={r.id} data-testid={`chat-quick-reply-${r.id}`} onClick={() => { setText(r.text.replaceAll("{primeiro_nome}", first)); setOpen(false); }}
                className="w-full rounded-lg px-3 py-2 text-left transition-colors hover:bg-[#1A2234]">
                <div className="text-xs font-semibold text-slate-200">{r.title}</div>
                <div className="line-clamp-2 text-[11px] text-slate-500">{r.text}</div>
              </button>
            ))}
            {!replies.length && <div className="px-3 py-4 text-center text-xs text-slate-500">Nenhuma resposta pronta. O admin cadastra em Configurações → Chat do site.</div>}
          </div>
        </PopoverContent>
      </Popover>
      <textarea data-testid="chat-input" rows={1} value={text} onChange={(e) => type(e.target.value)} maxLength={2000}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        placeholder="Escreva a resposta..." title="Enter envia · Shift+Enter quebra linha"
        className="max-h-32 min-h-[42px] flex-1 resize-none rounded-lg border border-[#1F293D] bg-[#0B0F17] px-3 py-2.5 text-sm text-slate-100 outline-none focus:border-sky-500" />
      <button data-testid="chat-send-btn" disabled={busy || !text.trim()} onClick={send} className="rounded-lg bg-sky-500 p-2.5 text-slate-950 transition-colors hover:bg-sky-400 disabled:opacity-40"><SendHorizontal className="h-4 w-4" /></button>
    </div>
  );
};
